import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {map, mergeMap, catchError, switchMap, take, withLatestFrom, first} from 'rxjs/operators';
import {of} from 'rxjs';
import { Product as Entity } from './product.model';
import {AppStoreEntityService} from "../app-store-entity.service";
import { ProductActions as EntityActions} from './product.actions'
import {ProductSelectors as Selectors} from "./product.selectors";
import {ProductService} from "./product.service";
import {IResultWithPageInfo} from "../app-store.interfaces";
import {select, Store} from "@ngrx/store";
import {AppState} from "../app-store.state";

@Injectable()
export class ProductEffects {
  loadAndSelectIfNecessary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadAndSelectIfNecessary),
      withLatestFrom(this.store.pipe(select(Selectors.selectAll))),
      switchMap(([action, entities]) => {
        const existingProduct = entities.find(x => x.id === action.productId);
        if (existingProduct !== undefined) {
          return of(EntityActions.loadAndSelectSuccess({entity: existingProduct, correlationId: action.correlationId}))
        }

        return this.appStoreEntityService.loadSingle<Entity>(this.EntityInfo, action.productId).pipe(
          map((entity: Entity) =>
            EntityActions.loadAndSelectSuccess({entity, correlationId :action.correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadAndSelectFailure({error,correlationId: action.correlationId}))
          )
        );
      })
    )
  );

  clone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.clone),
      switchMap(({entity, correlationId}) =>
        this.productService.clone(entity.id, true).pipe(
          map((entity: Entity) =>
            EntityActions.cloneSuccess({entity, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.cloneFailure({error, correlationId}))
          )
        )
      )
    ));

  loadPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadPage),
      switchMap(({page, criteria, correlationId}) =>
        this.appStoreEntityService.loadPage<Entity>(this.EntityInfo, page, criteria).pipe(
          map((resultWithPageInfo: IResultWithPageInfo<Entity>) =>
            EntityActions.loadPageSuccess({resultWithPageInfo, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadPageFailure({error, correlationId}))
          )
        )
      )
    )
  );

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.load),
      switchMap(({criteria,correlationId}) =>
        this.appStoreEntityService.load<Entity>(this.EntityInfo,criteria).pipe(
          map((entities: Entity[]) =>
            EntityActions.loadSuccess({entities, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadFailure({error, correlationId}))
          )
        )
      )
    )
  );

  loadSingle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadSingle),
      mergeMap(({entityId, correlationId}) =>
        this.appStoreEntityService.loadSingle<Entity>(this.EntityInfo, entityId).pipe(
          map((entity: Entity) =>
            EntityActions.loadSingleSuccess({entity, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.loadSingleFailure({error, correlationId}))
          )
        )
      )
    )
  );

  loadSingleIfNecessary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.loadSingleIfNecessary),
      mergeMap((action) =>
        this.store.pipe(
          select(Selectors.selectById(action.entityId)),
          take(1),
          switchMap((entity) => {
            if (entity)
              return of(EntityActions.loadSingleSuccess({entity, correlationId: action.correlationId}));

            const {entityId, correlationId} = action;
            return this.appStoreEntityService
              .loadSingle<Entity>(this.EntityInfo, entityId)
              .pipe(
                map((entity: Entity) =>
                  EntityActions.loadSingleSuccess({entity, correlationId})
                ),
                catchError((error) =>
                  of(EntityActions.loadSingleFailure({error, correlationId}))
                )
              );
          })
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.create),
      switchMap(({entity, correlationId}) =>
        this.appStoreEntityService.create<Entity>(this.EntityInfo, entity).pipe(
          map((result: Entity) =>
            EntityActions.createSuccess({entity: result, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.createFailure({error, correlationId}))
          )
        )
      )
    )
  );

  createMany = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.createMany),
      switchMap(({entities, correlationId}) =>
        this.appStoreEntityService.createMany<Entity[]>(this.EntityInfo, entities).pipe(
          map((result: Entity[]) =>
            EntityActions.createManySuccess({entities: result, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.createManyFailure({error, correlationId}))
          )
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.update),
      switchMap(({entity, correlationId}) =>
        this.appStoreEntityService.update(this.EntityInfo, entity).pipe(
          map((result: Entity) =>
            EntityActions.updateSuccess({entity: result, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.updateFailure({error, correlationId}))
          )
        )
      )
    )
  );

  updateMany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.updateMany),
      switchMap(({entities, correlationId}) =>
        this.appStoreEntityService.updateMany(this.EntityInfo, entities).pipe(
          map((results: Entity[]) =>
            EntityActions.updateManySuccess({entities: results, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.updateManyFailure({error, correlationId}))
          )
        )
      )
    )
  );

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.remove),
      switchMap(({entityId, correlationId}) =>
        this.appStoreEntityService.remove<Entity>(this.EntityInfo, entityId).pipe(
          map((entity) =>
            EntityActions.removeSuccess({entity, correlationId})
          ),
          catchError((error) =>
            of(EntityActions.removeFailure({error, correlationId}))
          )
        )
      )
    )
  );

  getTotalProductCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.getTotalProductCount),
      switchMap(() =>
        this.productService.getTotalCount().pipe(
          map((count) => EntityActions.updateTotalProductCount({ count })),
          catchError((error) => of(EntityActions.getTotalProductCountFailed({ error })))
        )
      )
    )
  );

  getProductColours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.getTotalProductColours),
      switchMap(() =>
        this.productService.getTotalProductColours().pipe(
          map((productColours) => EntityActions.getTotalProductColoursSuccess({ productColours })),
          catchError((error) => of(EntityActions.loadFailure({ error })))
        )
      )
    )
  );

  getProductExternalCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntityActions.getProductExternalCategories),
      switchMap(() =>
        this.productService.getProductExternalCategories().pipe(
          map((productExternalCategories) => EntityActions.getProductExternalCategoriesSuccess({ productExternalCategories })),
          catchError((error) => of(EntityActions.getProductExternalCategoriesFailure({ error })))
        )
      )
    )
  );

  EntityInfo = {modelName: "Product"};

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private appStoreEntityService: AppStoreEntityService,
    private productService: ProductService
  ) {
  }
}
